import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { getType } from 'typesafe-actions';

import store from '../../app/store';
import { spinnerStart, spinnerStop } from '../actions/spinner.actions';
import { logoutActions } from '../../features/account/login/login.actions';
import { showNotification } from '../actionCreators/dispatchNotification';

export const startSpinner = () => {
  store.dispatch({ type: getType(spinnerStart) });
}

export const stopSpinner = () => {
  store.dispatch({ type: getType(spinnerStop) });
}

let isRefreshing = false;
let pendingRequests: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
  config: AxiosRequestConfig;
}> = [];

function processPendingRequests(error: unknown = null) {
  pendingRequests.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      resolve(apiClient()(config));
    }
  });
  pendingRequests = [];
}

const noLogoutUrls = ['/account/current-token', '/account/logout', '/account/refresh-token'];

function apiClient(): AxiosInstance {
  let client = axios.create({
    baseURL: "/api/",
    timeout: 120000,
    headers: {
      'Content-Type': 'application/json',
    },
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
    withCredentials: true,
  });

  client.interceptors.request.use(async (config) => {
    await startSpinner();
    return config;
  }, error => {
    return Promise.reject(error);
  });

  client.interceptors.response.use(
    async response => {
      setTimeout(async () => { await stopSpinner(); }, 500);
      return response;
    },
    async error => {
      if (error.code === 'ECONNABORTED') {
        showNotification('The request timed out. Please try again.', 'error');
        await stopSpinner();
        return Promise.reject(error);
      }

      const originalRequest = error.config;
      const url = originalRequest?.url || '';

      if (error.response?.status === 401 && !noLogoutUrls.some(u => url.includes(u))) {
        if (originalRequest._retry) {
          store.dispatch({ type: getType(logoutActions.request) });
          showNotification('Your session timed out.', 'error');
          await stopSpinner();
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            pendingRequests.push({ resolve, reject, config: originalRequest });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          await axios.post('/api/account/refresh-token', null, { withCredentials: true });
          isRefreshing = false;
          processPendingRequests();
          await stopSpinner();
          return client(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          processPendingRequests(refreshError);
          store.dispatch({ type: getType(logoutActions.request) });
          showNotification('Your session timed out.', 'error');
          await stopSpinner();
          return Promise.reject(refreshError);
        }
      }

      await stopSpinner();
      return Promise.reject(error);
    }
  );

  return client;
}

export default apiClient;
